import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, BarChart3, Flag, Globe, CreditCard, ScrollText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/PageHeader";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

function Stat({ label, value }) {
  return <div className="rounded-2xl border bg-card p-5"><div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div><div className="font-display text-2xl font-bold mt-1">{value}</div></div>;
}

function StatsTab() {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return null;
  return (
    <div className="space-y-6">
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <Stat label={t("admin.totalUsers")} value={s.total_users} />
        <Stat label={t("admin.newUsers")} value={s.new_users_30d} />
        <Stat label={t("admin.activeUsers")} value={s.active_users_30d} />
        <Stat label={t("admin.paidSubs")} value={s.paid_subscriptions} />
        <Stat label={t("admin.conversion")} value={`${s.conversion_rate}%`} />
        <Stat label={t("admin.aiRequests")} value={s.ai_requests_total} />
        <Stat label={t("admin.creditsUsed")} value={s.credits_used_total} />
        <Stat label={t("admin.openReports")} value={s.open_reports} />
      </div>
      <div className="rounded-2xl border bg-card p-5">
        <h3 className="font-display font-semibold mb-3">{t("admin.usageByModule")}</h3>
        {s.usage_by_module.length === 0 && <p className="text-sm text-muted-foreground">—</p>}
        {s.usage_by_module.map((m) => (
          <div key={m.module} className="flex items-center justify-between py-2 border-b last:border-0 text-sm">
            <span className="capitalize">{m.module}</span><span className="text-muted-foreground">{m.events} · {m.credits} {t("common.credits")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersTab() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get(`/admin/users?q=${encodeURIComponent(q)}`).then(({ data }) => setUsers(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setRole = async (id, role) => { try { await api.put(`/admin/users/${id}/role`, { role }); load(); } catch (e) { toast.error(e.response?.data?.detail || t("common.error")); } };
  const setActive = async (id, is_active) => { try { await api.put(`/admin/users/${id}/active`, { is_active }); load(); } catch (e) { toast.error(e.response?.data?.detail || t("common.error")); } };
  const canRole = user?.role === "SUPER_ADMIN";
  return (
    <div>
      <div className="flex gap-2 mb-4"><Input placeholder={t("common.search")} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="admin-user-search" /><Button variant="outline" onClick={load}>{t("common.search")}</Button></div>
      <div className="rounded-2xl border bg-card divide-y overflow-x-auto">
        {users.map((u) => (
          <div key={u.id} className="flex items-center gap-3 p-3 text-sm min-w-[600px]" data-testid="admin-user-row">
            <div className="flex-1 min-w-0"><div className="font-medium truncate">{u.full_name || u.email}</div><div className="text-xs text-muted-foreground truncate">{u.email}</div></div>
            <Badge variant="secondary">{u.plan}</Badge>
            {canRole ? (
              <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                <SelectTrigger className="w-36 h-8" data-testid="admin-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["USER", "MODERATOR", "ADMIN", "SUPER_ADMIN"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            ) : <Badge>{u.role}</Badge>}
            <Button size="sm" variant={u.is_active ? "outline" : "default"} className="rounded-full h-8" onClick={() => setActive(u.id, !u.is_active)} data-testid="admin-toggle-active">
              {u.is_active ? t("admin.suspend") : t("admin.activate")}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportsTab() {
  const { t } = useI18n();
  const [reports, setReports] = useState([]);
  const load = () => api.get("/admin/reports?status=open").then(({ data }) => setReports(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const resolve = async (id, status, remove_target) => { await api.post(`/admin/reports/${id}/resolve`, { status, remove_target }); load(); };
  return (
    <div className="space-y-3">
      {reports.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center" data-testid="no-reports">—</p>}
      {reports.map((r) => (
        <div key={r.id} className="rounded-2xl border bg-card p-4" data-testid="report-row">
          <div className="flex items-center justify-between mb-2"><span className="text-xs uppercase tracking-wider text-primary font-semibold">{r.target_type}</span><span className="text-xs text-muted-foreground">{r.reason}</span></div>
          {r.target && <p className="text-sm text-muted-foreground mb-3 line-clamp-3 bg-muted rounded-lg p-2">{r.target.body}</p>}
          <div className="flex gap-2">
            <Button size="sm" variant="destructive" className="rounded-full" onClick={() => resolve(r.id, "actioned", true)} data-testid="report-remove">{t("admin.removeContent")}</Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => resolve(r.id, "dismissed", false)} data-testid="report-dismiss">{t("common.cancel")}</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function CountriesTab() {
  const { t } = useI18n();
  const [countries, setCountries] = useState([]);
  const load = () => api.get("/admin/countries").then(({ data }) => setCountries(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (code, field, value) => { await api.put(`/admin/countries/${code}`, { [field]: value }); load(); };
  return (
    <div className="rounded-2xl border bg-card divide-y overflow-x-auto">
      {countries.map((c) => (
        <div key={c.code} className="flex items-center gap-3 p-3 text-sm min-w-[560px]" data-testid="admin-country-row">
          <div className="flex-1"><span className="font-medium">{c.name_fr}</span> <span className="text-muted-foreground">({c.code}) · {c.currency} · {c.credit_system}</span></div>
          <Button size="sm" variant={c.credit_coach_enabled ? "default" : "outline"} className="rounded-full h-8" onClick={() => toggle(c.code, "credit_coach_enabled", !c.credit_coach_enabled)} data-testid="toggle-coach">Coach {c.credit_coach_enabled ? "ON" : "OFF"}</Button>
          <Button size="sm" variant={c.is_active ? "outline" : "default"} className="rounded-full h-8" onClick={() => toggle(c.code, "is_active", !c.is_active)} data-testid="toggle-country-active">{c.is_active ? t("admin.suspend") : t("admin.activate")}</Button>
        </div>
      ))}
    </div>
  );
}

function PlansTab() {
  const { t } = useI18n();
  const [plans, setPlans] = useState([]);
  const load = () => api.get("/admin/plans").then(({ data }) => setPlans(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async (code, price, credits) => { await api.put(`/admin/plans/${code}`, { price_monthly: Number(price), monthly_credits: Number(credits) }); toast.success(t("common.success")); load(); };
  return (
    <div className="rounded-2xl border bg-card divide-y">
      {plans.map((p) => (
        <div key={p.code} className="flex items-center gap-3 p-3 text-sm flex-wrap" data-testid="admin-plan-row">
          <span className="font-medium w-24">{p.name}</span>
          <label className="text-xs text-muted-foreground">Prix</label>
          <Input type="number" defaultValue={p.price_monthly} className="w-24 h-8" id={`price-${p.code}`} data-testid={`plan-price-${p.code}`} />
          <label className="text-xs text-muted-foreground">Crédits</label>
          <Input type="number" defaultValue={p.monthly_credits} className="w-24 h-8" id={`credits-${p.code}`} data-testid={`plan-credits-${p.code}`} />
          <Button size="sm" className="rounded-full h-8" onClick={() => save(p.code, document.getElementById(`price-${p.code}`).value, document.getElementById(`credits-${p.code}`).value)} data-testid={`save-plan-${p.code}`}>{t("common.save")}</Button>
        </div>
      ))}
    </div>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit-logs").then(({ data }) => setLogs(data)).catch(() => {}); }, []);
  return (
    <div className="rounded-2xl border bg-card divide-y text-sm">
      {logs.length === 0 && <p className="text-muted-foreground p-6 text-center">—</p>}
      {logs.map((l) => (
        <div key={l.id} className="p-3 flex items-center justify-between" data-testid="audit-row">
          <span><span className="font-medium">{l.action}</span> <span className="text-muted-foreground">{l.target_type} {l.target_id?.slice(0, 8)}</span></span>
          <span className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isModerator = user?.role === "MODERATOR";
  return (
    <div className="max-w-6xl mx-auto">
      <PageHeader title={t("admin.title")} />
      <Tabs defaultValue={isModerator ? "reports" : "stats"}>
        <TabsList className="mb-6 flex-wrap h-auto">
          {!isModerator && (<TabsTrigger value="stats" data-testid="admin-tab-stats"><BarChart3 className="h-4 w-4 mr-1.5" />{t("admin.stats")}</TabsTrigger>)}
          {!isModerator && (<TabsTrigger value="users" data-testid="admin-tab-users"><Users className="h-4 w-4 mr-1.5" />{t("admin.users")}</TabsTrigger>)}
          <TabsTrigger value="reports" data-testid="admin-tab-reports"><Flag className="h-4 w-4 mr-1.5" />{t("admin.reports")}</TabsTrigger>
          {!isModerator && (<TabsTrigger value="countries" data-testid="admin-tab-countries"><Globe className="h-4 w-4 mr-1.5" />{t("admin.countries")}</TabsTrigger>)}
          {!isModerator && (<TabsTrigger value="plans" data-testid="admin-tab-plans"><CreditCard className="h-4 w-4 mr-1.5" />{t("admin.plans")}</TabsTrigger>)}
          {!isModerator && (<TabsTrigger value="audit" data-testid="admin-tab-audit"><ScrollText className="h-4 w-4 mr-1.5" />{t("admin.audit")}</TabsTrigger>)}
        </TabsList>
        {!isModerator && (<TabsContent value="stats"><StatsTab /></TabsContent>)}
        {!isModerator && (<TabsContent value="users"><UsersTab /></TabsContent>)}
        <TabsContent value="reports"><ReportsTab /></TabsContent>
        {!isModerator && (<TabsContent value="countries"><CountriesTab /></TabsContent>)}
        {!isModerator && (<TabsContent value="plans"><PlansTab /></TabsContent>)}
        {!isModerator && (<TabsContent value="audit"><AuditTab /></TabsContent>)}
      </Tabs>
    </div>
  );
}
